import {OrderDepthViewModel} from "../models/OrderDepthViewModel";
import {ConsolidatedOrderDepthRecord, OrderDepthLevelRecord, OrderDepths} from "../types/OrderDepthRecord";
import {OrderDepthExpansionModel} from "../models/OrderDepthExpansionModel";
import {OrderDepthVolumeChange} from "../models/OrderDepthVolumeChange";
import {OrderDepthModel, OrderDepthModels, sortDepthByPrice} from "../models/OrderDepthModels";
import {sum} from "../../js/util/UtilFunctions";
import {Optional} from "../../js/util/Optional";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {NumberKeyed, Transform} from "../../types/GenericTypes";
import {OverlappingMarket} from "../models/OverlappingMarket";
import {ThemeType} from "../../types/UserConfig";

const DarkBaseGradientColourBid = "#2d866f";
const LightBaseGradientColourBid = "#cffbf0";

const DarkBaseGradientColourAsk = "#862d35";
const LightBaseGradientColourAsk = "#ffe5e8";

export class OrderDepthViewModelFactory {

    public static build(current: ConsolidatedOrderDepthRecord,
                        previous: ConsolidatedOrderDepthRecord | null,
                        expansion: OrderDepthExpansionModel,
                        rowsToDisplay: number,
                        theme: ThemeType): OrderDepthViewModel {
        const bids = current.bids;
        const totalBidVolume = bids.map((b) => b.volume).reduce(sum, 0);
        const previousBids = Optional.of(previous)
                                     .map((p) => p.bids)
                                     .orNull();

        const asks = current.asks;
        const totalAskVolume = asks.map((a) => a.volume).reduce(sum, 0);
        const previousAsks = Optional.of(previous)
                                     .map((p) => p.asks)
                                     .orNull();

        const overlappingMarket = OverlappingMarket.calculate(bids, asks);
        const matchPrice = Optional.of(overlappingMarket)
                                   .map((om) => om.matchPrice)
                                   .orNull();

        const bidModels = this.createBids(bids, previousBids, matchPrice, expansion, theme);
        const displayedBids = bidModels.slice(0, rowsToDisplay);
        const askModels = this.createAsks(asks, previousAsks, matchPrice, expansion, theme);
        const displayedAsks = askModels.slice(0, rowsToDisplay);

        const hasHiddenRows = (bidModels.length > displayedBids.length) || (askModels.length > displayedAsks.length);

        return OrderDepthViewModel.create(displayedBids, totalBidVolume, displayedAsks, totalAskVolume, hasHiddenRows);
    }

    private static createBids(bids: OrderDepths,
                              previousBids: OrderDepths | null,
                              matchPrice: number | null,
                              expansion: OrderDepthExpansionModel,
                              theme: ThemeType): OrderDepthModels {
        const bidVolumes = bids.map((b) => b.volume);
        const maxBidVolume = Math.max(...bidVolumes);
        const previousBidsByPrice = Optional.of(previousBids)
                                            .map((pb) => ArrayUtil.toMap(pb, (b) => b.price))
                                            .orNull();

        const volumeChangeFn = this.volumeChange(previousBidsByPrice);

        const models = bids.map((b) => {
            const hasMatch = (matchPrice != null) && (b.price >= matchPrice);
            const change = volumeChangeFn(b);
            const collapsed = !expansion.isBidExpanded(b.price);
            return this.createBid(b, maxBidVolume, hasMatch, change, collapsed, theme);
        });

        return models.sort(sortDepthByPrice())
                     .reverse();
    }

    private static createBid(record: OrderDepthLevelRecord,
                             maxBidVolume: number,
                             hasMatch: boolean,
                             change: OrderDepthVolumeChange,
                             collapsed: boolean,
                             theme: ThemeType): OrderDepthModel {
        const percentOfMax = Math.round((record.volume / maxBidVolume) * 100);
        const style = hasMatch ? this.defaultStyle() : this.bidStyle(percentOfMax, theme);
        return OrderDepthModel.create(record, style, collapsed, hasMatch, change);
    }

    private static bidStyle(percentOfMax: number, theme: ThemeType): string {
        const colour = theme === "light" ? LightBaseGradientColourBid : DarkBaseGradientColourBid;
        return `linear-gradient(90deg, transparent 0% ${100 - percentOfMax}%, ${colour}30 ${100 - percentOfMax}%, ${colour}FF)`;
    }

    private static createAsks(asks: OrderDepths,
                              previousAsks: OrderDepths | null,
                              matchPrice: number | null,
                              expansion: OrderDepthExpansionModel,
                              theme: ThemeType): OrderDepthModels {
        const askVolumes = asks.map((a) => a.volume);
        const maxAskVolume = Math.max(...askVolumes);

        const previousAsksByPrice = Optional.of(previousAsks)
                                            .map((pb) => ArrayUtil.toMap(pb, (b) => b.price))
                                            .orNull();
        const volumeChangeFn = this.volumeChange(previousAsksByPrice);

        const models = asks.map((a) => {
            const hasMatch = (matchPrice != null) && (a.price <= matchPrice);
            const change = volumeChangeFn(a);
            const collapsed = !expansion.isAskExpanded(a.price);
            return this.createAsk(a, maxAskVolume, hasMatch, change, collapsed, theme);
        });

        return models.sort(sortDepthByPrice());
    }

    private static createAsk(record: OrderDepthLevelRecord,
                             maxBidVolume: number,
                             hasMatch: boolean,
                             change: OrderDepthVolumeChange,
                             collapsed: boolean,
                             theme: ThemeType): OrderDepthModel {
        const percentOfMax = Math.round((record.volume / maxBidVolume) * 100);
        const style = hasMatch ? this.defaultStyle() : this.askStyle(percentOfMax, theme);
        return OrderDepthModel.create(record, style, collapsed, hasMatch, change);
    }

    private static askStyle(percentOfMax: number, theme: ThemeType): string {
        const colour = theme === "light" ? LightBaseGradientColourAsk : DarkBaseGradientColourAsk;
        return `linear-gradient(90deg, ${colour}FF, ${colour}30 ${percentOfMax}%, transparent ${percentOfMax}% 100%)`;
    }

    private static defaultStyle() {
        return "unset";
    }

    private static volumeChange(previousRecords: NumberKeyed<OrderDepthLevelRecord> | null): Transform<OrderDepthLevelRecord, OrderDepthVolumeChange> {
        return (record) => {
            // If we don't have previous records to compare against then we say no change as this is the first load
            if (previousRecords == null) {
                return OrderDepthVolumeChange.NO_CHANGE;
            }

            const previous = previousRecords[record.price];
            if (previous == null) {
                return OrderDepthVolumeChange.UP;
            } else if (previous.volume < record.volume) {
                return OrderDepthVolumeChange.UP;
            } else if (previous.volume > record.volume) {
                return OrderDepthVolumeChange.DOWN;
            } else {
                return OrderDepthVolumeChange.NO_CHANGE;
            }
        };
    }
}
